import { memo, useMemo, useState } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { MICROCOPY_SIZE } from 'HubStyleTokens/sizes';
import { CANDY_APPLE, SLINKY } from 'HubStyleTokens/colors';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UIIcon from 'UIComponents/icon/UIIcon';
import UISelect from 'UIComponents/input/UISelect';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { updateEngagementData } from 'conversations-calling-message/timeline-call-metadata/common/clients/updateEngagementData';
import { getCallDisposition, getCRMObjectId, getResult } from '../../call-message/operators/callMessageGetters';
import { logNetworkError } from 'calling-error-reporting/report/error';
import { useFetchCallDispositions } from '../hooks/useFetchCallDispositions';
import { getDefaultOptions } from '../utils/getDefaultOptions';
import { useFetchCallSettings } from '../hooks/useFetchCallSettings';
import { OUTGOING } from 'conversations-message-history/common-message-format/constants/messageDirections';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledWrapper = styled.div.withConfig({
  displayName: "MutableCallDisposition__StyledWrapper",
  componentId: "sc-7pz42h-0"
})(["display:flex;padding-right:24px;"]);
const StyledUIFormLabel = styled(UIFormLabel).withConfig({
  displayName: "MutableCallDisposition__StyledUIFormLabel",
  componentId: "sc-7pz42h-1"
})(["&&{font-size:", ";color:", ";font-weight:normal;}"], MICROCOPY_SIZE, SLINKY);
const StyledUIIcon = styled(UIIcon).withConfig({
  displayName: "MutableCallDisposition__StyledUIIcon",
  componentId: "sc-7pz42h-2"
})(["&&{padding-top:42px;padding-left:4px;}"]);
const MutableCallDisposition = ({
  callDirection,
  callMessage,
  handleEngagementDataUpdated
}) => {
  const initialCallDisposition = getResult(getCallDisposition(callMessage));
  const [selectedDisposition, setSelectedDisposition] = useState(initialCallDisposition);
  const {
    dispositionsList,
    loading
  } = useFetchCallDispositions();
  const {
    callOutcomeRequired
  } = useFetchCallSettings();
  const renderCallOutcomeRequiredTooltip = useMemo(() => callOutcomeRequired && callDirection === OUTGOING && !selectedDisposition, [callDirection, callOutcomeRequired, selectedDisposition]);
  const engagementId = getCRMObjectId(callMessage);
  const handleSelectDisposition = event => {
    const option = event.target.value;
    const selectedDispositionId = option.value;
    const selectedDispositionText = option.text;
    if (selectedDispositionText !== selectedDisposition) {
      setSelectedDisposition(selectedDispositionText);
      const payload = {
        properties: [{
          name: 'hs_call_disposition',
          value: selectedDispositionId
        }]
      };
      updateEngagementData(engagementId, payload).then(() => {
        handleEngagementDataUpdated({
          engagementId
        });
      }).catch(networkError => {
        logNetworkError({
          error: new Error('Error updating call outcome'),
          networkError
        });
      });
    }
  };
  return /*#__PURE__*/_jsxs(StyledWrapper, {
    children: [/*#__PURE__*/_jsx(UIFormControl, {
      className: "p-top-1 p-bottom-4",
      label: "Outcome",
      FormLabel: StyledUIFormLabel,
      children: /*#__PURE__*/_jsx(UISelect, {
        buttonUse: "link",
        "data-selenium-test": 'select-call-disposition-dropdown',
        isLoading: !dispositionsList && loading,
        menuWidth: "auto",
        onSelectedOptionChange: handleSelectDisposition,
        options: dispositionsList || getDefaultOptions(),
        placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-calling-message.timeline-call-metadata.call-dispositions.placeholder"
        }),
        searchable: false,
        value: selectedDisposition
      })
    }), renderCallOutcomeRequiredTooltip && /*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "conversations-calling-message.timeline-call-metadata.call-dispositions.required"
      }),
      children: /*#__PURE__*/_jsx(StyledUIIcon, {
        name: "warning",
        color: CANDY_APPLE,
        size: 20
      })
    })]
  });
};
export default /*#__PURE__*/memo(MutableCallDisposition);